import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Sparkles, Calendar, Users, IndianRupee, Clock, ArrowRight, Wand2,
    Car, UtensilsCrossed, Film, ShoppingBag, Heart, Coffee, CheckCircle2, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useMall } from '@/lib/mall';
import { useAuth } from '@/lib/auth';
import { proxyImg } from '@/lib/imageProxy';

const VIBES = [
    { key: 'date_night', label: 'Date Night', icon: Heart, blurb: 'Intimate dinners, dim lighting, a memorable film' },
    { key: 'family_day', label: 'Family Day Out', icon: Users, blurb: 'Animated movies, kid brands, family dining' },
    { key: 'shopping_spree', label: 'Shopping Spree', icon: ShoppingBag, blurb: 'Hit the top brands across every floor' },
    { key: 'quick_lunch', label: 'Quick Lunch', icon: Coffee, blurb: 'In, eat, out — 60 minutes flat' },
    { key: 'kids_day', label: 'Kids Day', icon: Sparkles, blurb: 'Play zones, ice cream, animated film' },
    { key: 'birthday', label: 'Birthday Bash', icon: Sparkles, blurb: 'Cake, a fun film, photo spots' },
    { key: 'solo_treat', label: 'Solo Treat', icon: Heart, blurb: 'Self-care, calm cafe, thoughtful film' },
];

const STEP_ICONS = {
    parking: Car,
    food: UtensilsCrossed,
    movie: Film,
    shopping: ShoppingBag,
    service: Sparkles,
    relax: Coffee,
};

const STEP_COLORS = {
    parking: 'bg-inorbit-blue text-white',
    food: 'bg-inorbit-orange text-white',
    movie: 'bg-inorbit-yellow text-inorbit-blue',
    shopping: 'bg-inorbit-blue text-white',
    service: 'bg-inorbit-blue text-white',
    relax: 'bg-inorbit-orange text-white',
};

export default function PlanMyVisit() {
    const { selectedMall, allMalls, choose } = useMall();
    const { type } = useAuth();
    const [vibe, setVibe] = useState('date_night');
    const [groupSize, setGroupSize] = useState(2);
    const [budget, setBudget] = useState(2500);
    const [duration, setDuration] = useState(4);
    const [start, setStart] = useState('evening');
    const [dietary, setDietary] = useState('');
    const [plan, setPlan] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [bookingPlan, setBookingPlan] = useState(false);
    const [vehicle, setVehicle] = useState('');
    const [executed, setExecuted] = useState(null);

    const generate = async () => {
        if (!selectedMall) { toast.error('Pick a mall first'); return; }
        setPlan(null);
        setExecuted(null);
        setGenerating(true);
        try {
            const { data } = await api.post('/plan/generate', {
                mall_slug: selectedMall.slug, vibe,
                group_size: groupSize, budget_per_person: budget,
                duration_hours: duration, preferred_start: start,
                dietary_prefs: dietary,
                interests: ['dining', 'movie', 'shopping'],
            });
            setPlan(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Plan generation failed');
        } finally {
            setGenerating(false);
        }
    };

    const execute = async () => {
        if (type !== 'customer') { toast.error('Sign in to auto-book your plan'); return; }
        if (!vehicle.trim() || vehicle.length < 4) { toast.error('Enter your vehicle number for the parking step'); return; }
        setBookingPlan(true);
        try {
            const { data } = await api.post('/plan/execute', {
                plan_id: plan.plan_id, auto_book_parking: true,
                parking_duration: '4h', vehicle_number: vehicle.trim(),
            });
            setExecuted(data);
            toast.success(`Plan booked — ${data.bookings.length} confirmations sent`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Auto-booking failed');
        } finally { setBookingPlan(false); }
    };

    const totalSpend = (plan?.steps || []).reduce((s, x) => s + (x.estimated_spend || 0), 0);

    return (
        <div data-testid="plan-page" className="bg-cream min-h-screen">
            {/* Hero */}
            <section className="relative bg-inorbit-blue-deeper text-white pt-28 pb-12 overflow-hidden" data-testid="plan-hero">
                <div className="absolute -top-32 -left-32 w-[480px] h-[480px] border-2 border-inorbit-yellow/15 rounded-full orbit-slow" />
                <div className="absolute -bottom-24 -right-24 w-[360px] h-[360px] border border-inorbit-orange/20 rounded-full orbit-fast" />
                <div className="relative max-w-6xl mx-auto px-6 md:px-12">
                    <div className="overline text-inorbit-yellow mb-3 flex items-center gap-2"><Wand2 size={12} /> Powered by Claude Sonnet 4.6</div>
                    <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-none" style={{ color: 'white' }}>
                        Plan my <span className="script-tag text-inorbit-yellow">Inorbit visit.</span>
                    </h1>
                    <p className="mt-5 max-w-2xl text-white/85 text-base md:text-lg leading-relaxed">
                        Tell us the vibe. Our AI concierge stitches together parking, dining, movies and store hops into a personalised itinerary — and pre-books everything in one tap.
                    </p>
                </div>
            </section>

            <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 grid lg:grid-cols-5 gap-8">

                {/* LEFT FORM */}
                <div className="lg:col-span-2 card-luxury p-7 h-fit sticky top-24" data-testid="plan-form">
                    <div className="overline mb-4">Tell us about your visit</div>

                    {/* Mall selector */}
                    <label className="overline mb-2 block text-[10px]">Mall</label>
                    <div className="flex flex-wrap gap-1.5 mb-6">
                        {allMalls.map((m) => (
                            <button key={m.id} onClick={() => choose(m.slug)} className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider font-semibold transition-all ${selectedMall?.slug === m.slug ? 'bg-inorbit-yellow text-inorbit-blue' : 'bg-cream text-inorbit-blue hover:bg-inorbit-blue hover:text-white'}`} data-testid={`plan-mall-${m.slug}`}>
                                {m.short_name}
                            </button>
                        ))}
                    </div>

                    {/* Vibe */}
                    <label className="overline mb-2 block text-[10px]">Vibe</label>
                    <div className="grid grid-cols-2 gap-2 mb-6">
                        {VIBES.map((v) => {
                            const I = v.icon;
                            return (
                                <button key={v.key} onClick={() => setVibe(v.key)} className={`p-3 rounded-md border-2 text-left transition-all ${vibe === v.key ? 'border-inorbit-orange bg-cream' : 'border-border-subtle hover:border-inorbit-blue'}`} data-testid={`vibe-${v.key}`}>
                                    <I size={16} className={vibe === v.key ? 'text-inorbit-orange mb-1.5' : 'text-text-secondary mb-1.5'} />
                                    <div className="text-sm font-serif text-inorbit-blue leading-tight">{v.label}</div>
                                    <div className="text-[10px] text-text-tertiary mt-0.5 leading-snug line-clamp-2">{v.blurb}</div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Group, budget, duration */}
                    <div className="grid grid-cols-2 gap-3 mb-6">
                        <div>
                            <label className="overline mb-2 block text-[10px] flex items-center gap-1.5"><Users size={11} /> Group</label>
                            <input type="number" min={1} max={20} value={groupSize} onChange={(e) => setGroupSize(parseInt(e.target.value) || 1)} className="input-box w-full" data-testid="plan-group" />
                        </div>
                        <div>
                            <label className="overline mb-2 block text-[10px] flex items-center gap-1.5"><Clock size={11} /> Hours</label>
                            <input type="number" min={1} max={12} value={duration} onChange={(e) => setDuration(parseInt(e.target.value) || 1)} className="input-box w-full" data-testid="plan-hours" />
                        </div>
                    </div>
                    <label className="overline mb-2 block text-[10px] flex items-center gap-1.5"><IndianRupee size={11} /> Budget per person — ₹{budget.toLocaleString('en-IN')}</label>
                    <input type="range" min={500} max={10000} step={100} value={budget} onChange={(e) => setBudget(parseInt(e.target.value))} className="w-full mb-6 accent-inorbit-orange" data-testid="plan-budget" />

                    <label className="overline mb-2 block text-[10px]">Preferred start</label>
                    <div className="grid grid-cols-4 gap-1.5 mb-6">
                        {['morning', 'afternoon', 'evening', 'night'].map((t) => (
                            <button key={t} onClick={() => setStart(t)} className={`px-2 py-2 rounded-md text-[10px] uppercase tracking-wider font-semibold transition-all ${start === t ? 'bg-inorbit-blue text-white' : 'bg-cream text-inorbit-blue hover:bg-inorbit-orange hover:text-white'}`} data-testid={`plan-time-${t}`}>{t}</button>
                        ))}
                    </div>

                    <label className="overline mb-2 block text-[10px]">Dietary / preferences (optional)</label>
                    <input value={dietary} onChange={(e) => setDietary(e.target.value)} placeholder="vegetarian, no spice…" className="input-box w-full mb-6" data-testid="plan-dietary" />

                    <button onClick={generate} disabled={generating} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="plan-generate-btn">
                        {generating ? <><RefreshCw size={14} className="animate-spin" /> Crafting your visit…</> : <><Wand2 size={14} /> Generate my itinerary</>}
                    </button>
                </div>

                {/* RIGHT — PLAN OUTPUT */}
                <div className="lg:col-span-3" data-testid="plan-output">
                    <AnimatePresence mode="wait">
                        {!plan && !generating && (
                            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card-luxury p-12 text-center" data-testid="plan-empty">
                                <Wand2 size={42} strokeWidth={1.2} className="text-inorbit-orange mx-auto mb-4" />
                                <div className="font-serif text-2xl text-inorbit-blue mb-2">Your concierge is ready</div>
                                <div className="text-text-secondary text-sm max-w-md mx-auto leading-relaxed">
                                    Pick a vibe and tap <strong className="text-inorbit-blue">Generate</strong>. We'll combine real restaurants, real movies, and real parking at {selectedMall?.short_name || 'your mall'} into a step-by-step plan.
                                </div>
                            </motion.div>
                        )}
                        {generating && (
                            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card-luxury p-12 text-center" data-testid="plan-loading">
                                <RefreshCw size={42} strokeWidth={1.2} className="text-inorbit-orange mx-auto mb-4 animate-spin" />
                                <div className="font-serif text-2xl text-inorbit-blue mb-2">Curating your experience…</div>
                                <div className="text-text-secondary text-sm">Tasting menus, checking showtimes, reserving the best valet bay.</div>
                            </motion.div>
                        )}
                        {plan && (
                            <motion.div key="plan" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="plan-result">
                                <div className="card-luxury p-8 mb-6 bg-gradient-to-br from-inorbit-blue to-inorbit-blue-deeper text-white">
                                    <div className="overline text-inorbit-yellow mb-2">Your curated visit</div>
                                    <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-3" style={{ color: 'white' }}>{plan.title}</h2>
                                    <div className="text-inorbit-yellow text-base italic font-serif mb-4">{plan.tagline}</div>
                                    <div className="text-white/85 text-sm leading-relaxed mb-6">{plan.summary}</div>
                                    <div className="flex flex-wrap gap-3 text-sm">
                                        <span className="bg-white/10 px-3 py-1.5 rounded-full"><strong>{plan.steps?.length || 0}</strong> steps</span>
                                        <span className="bg-white/10 px-3 py-1.5 rounded-full">≈ ₹<strong>{totalSpend.toLocaleString('en-IN')}</strong> for {groupSize}</span>
                                        <span className="bg-white/10 px-3 py-1.5 rounded-full">{duration} hours</span>
                                    </div>
                                </div>

                                {/* Timeline */}
                                <ol className="relative border-l-2 border-inorbit-yellow/30 ml-3 mb-6" data-testid="plan-timeline">
                                    {(plan.steps || []).map((step, i) => {
                                        const Icon = STEP_ICONS[step.category] || Sparkles;
                                        const ref = step.restaurant || step.movie || step.parking;
                                        return (
                                            <li key={i} className="ml-7 mb-6" data-testid={`plan-step-${i}`}>
                                                <span className={`absolute -left-[18px] w-9 h-9 rounded-full border-2 border-white flex items-center justify-center ${STEP_COLORS[step.category] || 'bg-inorbit-blue text-white'}`}>
                                                    <Icon size={15} strokeWidth={2} />
                                                </span>
                                                <div className="card-luxury p-5">
                                                    <div className="flex items-baseline justify-between gap-2 mb-2">
                                                        <div className="flex items-center gap-2">
                                                            <span className="overline text-[10px]">{step.category}</span>
                                                            <span className="text-[11px] tracking-widest text-inorbit-orange font-bold">{step.time}</span>
                                                            {step.duration_min && <span className="text-[10px] text-text-tertiary">· {step.duration_min} min</span>}
                                                        </div>
                                                        {step.estimated_spend > 0 && <span className="font-serif text-lg text-inorbit-blue">₹{step.estimated_spend.toLocaleString('en-IN')}</span>}
                                                    </div>
                                                    <div className="font-serif text-xl text-inorbit-blue mb-1.5 leading-tight">{step.title}</div>
                                                    <div className="text-sm text-text-secondary italic leading-relaxed">{step.why}</div>
                                                    {step.restaurant && (
                                                        <div className="mt-4 flex gap-3 bg-cream p-3 rounded-md">
                                                            <img src={step.restaurant.image} alt={step.restaurant.name} className="w-20 h-20 rounded object-cover shrink-0" />
                                                            <div className="text-xs text-text-secondary">
                                                                <div className="text-inorbit-blue font-semibold text-sm">{step.restaurant.name}</div>
                                                                <div>{step.restaurant.cuisine} · {step.restaurant.price_range}</div>
                                                                <div className="text-text-tertiary">⭐ {step.restaurant.rating} · {step.restaurant.vibe}</div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {step.movie && (
                                                        <div className="mt-4 flex gap-3 bg-cream p-3 rounded-md">
                                                            <img src={proxyImg(step.movie.poster)} alt={step.movie.title} className="w-16 h-24 rounded object-cover shrink-0" />
                                                            <div className="text-xs text-text-secondary">
                                                                <div className="text-inorbit-blue font-semibold text-sm">{step.movie.title}</div>
                                                                <div>{step.movie.format} · {step.movie.lang} · {step.movie.duration_min}m</div>
                                                                <div className="text-text-tertiary">⭐ IMDb {step.movie.imdb_rating}</div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ol>

                                {/* Auto-book */}
                                {!executed && (
                                    <div className="card-luxury p-6 bg-inorbit-yellow border-inorbit-yellow" data-testid="auto-book-card">
                                        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                                            <div>
                                                <div className="overline text-inorbit-blue">One-tap booking</div>
                                                <div className="font-serif text-2xl text-inorbit-blue">Auto-book this plan</div>
                                            </div>
                                            <div className="text-inorbit-blue/80 text-xs">Parking · Movie · Table — all in one go.</div>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <input value={vehicle} onChange={(e) => setVehicle(e.target.value.toUpperCase())} placeholder="MH 02 AB 1234" className="input-box flex-1 min-w-[200px] uppercase font-mono tracking-widest bg-white" maxLength={12} data-testid="auto-book-vehicle" />
                                            <button onClick={execute} disabled={bookingPlan} className="btn-primary disabled:opacity-50" data-testid="auto-book-btn">
                                                {bookingPlan ? 'Booking…' : 'Book it all'} <ArrowRight size={14} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {executed && (
                                    <div className="card-luxury p-7 border-2 border-emerald-500/30 bg-emerald-50" data-testid="auto-book-success">
                                        <CheckCircle2 size={28} className="text-emerald-600 mb-3" />
                                        <div className="font-serif text-2xl text-inorbit-blue mb-2">You're all set</div>
                                        <div className="text-sm text-text-secondary mb-4">{executed.bookings.length} confirmations · ₹{executed.total.toLocaleString('en-IN')} total. Check <Link to="/profile" className="text-inorbit-orange underline">my bookings</Link>.</div>
                                        <div className="grid sm:grid-cols-2 gap-2 text-xs">
                                            {executed.bookings.map((b, i) => (
                                                <div key={i} className="bg-white p-3 rounded border border-emerald-200">
                                                    <div className="overline text-emerald-700">{b.step}</div>
                                                    <div className="font-mono text-inorbit-blue mt-1">{b.booking.qr_code}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}
